/**
 * The migration state of UX D11/D23 (CU-869fhuhy6), read from
 * `GET /api/repricing/migration` (`@services/useMigration`).
 *
 *   <MigrationBanner where="strategies" | "setup"/>
 *       the one-time `Alert info` over Strategies and Setup while the
 *       migration is pending: links to the read-only old windows and the
 *       report dialog. No button validates it — the human OK is an
 *       audit-log entry the engineer writes with the migration script.
 *   <LegacyReadOnlyAlert/>
 *       the `Alert warning outlined` the old windows (Schedule, Rate cards)
 *       show while they are kept.
 *   <MigrationReportDialog report={…}/>
 *       the report the migration produced, painted as it came (plan § 6.3).
 *
 * This banner and the report are the one place that still names "schedule
 * rules" and "rate cards": they explain the mapping from the old model
 * (UX v3 D23, the exception to the prose sweep).
 */
import React, { useState } from 'react'
import {
	Box, Button, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader, AppNote, secondaryButtonSx } from '@components'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'
import { dsColorNew } from '@palette'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import { headCellSx, codeSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import { SiteCatalogProvider, useSiteCatalog } from 'Pages/DsRepricing/Setup/@services/sites'
import { useMigration } from 'Pages/DsRepricing/@services/useMigration'

export { useMigrationPending } from 'Pages/DsRepricing/@services/useMigration'

export const MIGRATION_DATE = '16 Oct 2026'

// A link inside a note, in the app's blue
const linkSx = { color: dsColorNew.appBlue, fontWeight: 600, fontSize: 13, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }

const fmtWhen = (iso) => {
	if (!iso) return MIGRATION_DATE
	const d = new Date(iso)
	return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function MigrationBanner({ where = 'strategies', sx }) {
	const m = useMigration()
	const [report, setReport] = useState(false)
	if (m.pending !== true) return null
	const r = m.report
	const from = r?.from || {}
	const hand = Array.isArray(r?.needs_hand) ? r.needs_hand : []
	return (
		<>
			<AppNote sx={{ mb: 2, ...sx }}>
				<Typography sx={{ fontWeight: 600, fontSize: 13, color: dsColorNew.textTitle, mb: 0.5 }}>
					The rate cards became Products and the scheduler went into each strategy — review the mapping
				</Typography>
				<Typography sx={{ fontSize: 13, lineHeight: 1.5, color: dsColorNew.textDark }}>
					{m.error
						? <>The migration is pending validation; its state could not be read right now ({m.error}). </>
						: <>Migrated on {fmtWhen(r?.at)} from {from.rules ?? '…'} schedule rule{from.rules === 1 ? '' : 's'} and{' '}
							{from.rate_cards ?? '…'} rate card{from.rate_cards === 1 ? '' : 's'} into {from.strategies ?? '…'} strateg{from.strategies === 1 ? 'y' : 'ies'} — pending your validation. </>}
					Schedule and Rate cards are read-only until Curro and Thibault confirm the equivalence; nothing old is deleted before that.
					{hand.length > 0 && (
						<> <strong>{hand.length} need{hand.length === 1 ? 's' : ''} a hand</strong>: {hand.join('; ')}.</>
					)}
				</Typography>
				<Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1.25, alignItems: 'center' }}>
					<Button size="small" variant="outlined" onClick={() => setReport(true)} disabled={!r}
					        title={r ? undefined : 'The migration has not written its report yet'}
					        sx={{ ...secondaryButtonSx, height: 32 }}>
						Open the migration report
					</Button>
					<Box component={RouterLink} to={DS_REPRICING_ROUTES.SCHEDULE_LEGACY_PATH} sx={linkSx}>
						Schedule (legacy, read-only)
					</Box>
					<Box component={RouterLink} to={`${DS_REPRICING_ROUTES.SETUP_PATH}?tab=rate-cards`} sx={linkSx}>
						Rate cards (legacy, read-only)
					</Box>
					{where !== 'strategies' && (
						<Box component={RouterLink} to={DS_REPRICING_ROUTES.STRATEGIES_PATH} sx={linkSx}>
							See the strategies
						</Box>
					)}
				</Box>
			</AppNote>
			{report && r && <MigrationReportDialog report={r} onClose={() => setReport(false)}/>}
		</>
	)
}

export function LegacyReadOnlyAlert({ what = 'Edit the strategy instead.' }) {
	return (
		<AppNote severity="warning" sx={{ mb: 2 }}>
			<strong>Read-only</strong> — migrated into Products and Strategies on {MIGRATION_DATE}. {what}{' '}
			This window goes once the migration is validated.
		</AppNote>
	)
}

export function MigrationReportDialog({ report, onClose }) {
	return (
		<SiteCatalogProvider>
			<ReportInner report={report} onClose={onClose}/>
		</SiteCatalogProvider>
	)
}

// A section of the report, in the small-caps title of the app's detail blocks
const H = ({ n, children }) => (
	<Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
	                  color: dsColorNew.textMuted, mt: 2.5, mb: 1 }}>{n} · {children}</Typography>
)
const small = { fontSize: 13, color: dsColorNew.textDark }
const muted = { fontSize: 12, color: dsColorNew.textMuted }
const onOff = (status) => (status === 'enabled' ? 'On' : status === 'draft' ? 'Draft' : 'Off')
const compareWords = (to) => (!to ? '—' : to.mode === 'every' ? 'every kind' : to.mode === 'specific'
	? `a specific kind: ${[to.tariff_type == null ? null : (to.tariff_type ? 'Prepaid' : 'Non prepaid'), to.insurance == null ? null : `insurance ${to.insurance}`].filter(Boolean).join(' · ') || 'any'}`
	: 'same kind as the product')

function ReportInner({ report: r, onClose }) {
	const { labelOf } = useSiteCatalog()
	const strategies = Array.isArray(r.strategies) ? r.strategies : []
	const products = Array.isArray(r.products) ? r.products : []
	const eq = r.equivalence || null
	const cell = { fontSize: 12.5, py: 0.75, verticalAlign: 'top' }
	return (
		<AppDialog open onClose={onClose} maxWidth="lg" fullWidth>
			<AppDialogHeader icon="clipboard-list" title="Migration report"
			                 subtitle={`${fmtWhen(r.at)}${r.by ? ` · by ${r.by}` : ''}`} onClose={onClose}/>
			<AppDialogBody sx={dialogBodySx}>
				<Typography sx={muted}>
					Written by the migration: old rate cards → product rows, old schedule rules → each strategy's own cadence,
					the two compare switches → "compared against". Nothing old is deleted until this is validated.
				</Typography>

				<H n={1}>Strategies (old → new)</H>
				<Table size="small" sx={{ '& td, & th': cell }}>
					<TableHead><TableRow sx={{ '& th': headCellSx }}>
						<TableCell>Strategy</TableCell><TableCell>Product</TableCell><TableCell>Sites</TableCell>
						<TableCell>Compared against</TableCell><TableCell>Cadence</TableCell><TableCell>On/Off · notes</TableCell>
					</TableRow></TableHead>
					<TableBody>
						{strategies.length === 0 && <TableRow><TableCell colSpan={6} sx={{ color: dsColorNew.textMuted }}><em>No strategy in the report.</em></TableCell></TableRow>}
						{strategies.map(s => (
							<TableRow key={s.id}>
								<TableCell sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>{s.name}</TableCell>
								<TableCell>{s.product ? <strong>{s.product.name}</strong> : <Box component="em" sx={{ color: dsColorNew.noteAmber }}>no product — choose one</Box>}</TableCell>
								<TableCell>
									{(s.sites || []).map(labelOf).join(', ') || <em>every site of the product</em>}
									{(s.removed_sites || []).length > 0 && (
										<Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber }}>removed: {s.removed_sites.map(labelOf).join(', ')}</Typography>
									)}
								</TableCell>
								<TableCell>
									{compareWords(s.compare?.to)}
									{s.compare?.review && <Chip size="small" label="review" sx={{ ...toneChipSx('warn'), ml: 0.5, height: 20 }}/>}
								</TableCell>
								<TableCell>
									{s.cadence?.from_rule
										? <>from rule <strong>{s.cadence.from_rule.name}</strong> <Chip size="small" label={s.cadence.from_rule.active ? 'active' : 'paused'} sx={{ ...toneChipSx(s.cadence.from_rule.active ? 'ok' : 'neutral'), ml: 0.5, height: 20 }}/></>
										: <em>factory proposal</em>}
									{s.cadence?.factory && <Typography sx={muted}>nobody reviewed it yet</Typography>}
								</TableCell>
								<TableCell>
									<Chip size="small" label={`${onOff(s.status?.from)} → ${onOff(s.status?.to)}`}
									      sx={{ ...toneChipSx(s.status?.to === 'enabled' ? 'ok' : 'neutral'), mr: 0.5 }}/>
									{(s.notes || []).join(' · ')}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>

				<H n={2}>Products created from the rate cards</H>
				{products.length === 0 ? <Typography sx={small}><em>None — no rate card had a write pricelist.</em></Typography>
				: products.map(p => (
					<Box key={p.id} sx={{ ...small, mb: 1 }}>
						<strong>{p.name}</strong>
						{p.kind && <Box component="span" sx={{ color: dsColorNew.textMuted }}> ({p.kind})</Box>}
						{p.rename_me && <Chip size="small" label="rename me" sx={{ ...toneChipSx('warn'), ml: 0.75, height: 20 }}/>}
						{(p.rows || []).map((row, i) => (
							<Typography key={i} sx={{ fontSize: 12.5, pl: 2, color: row.invalid ? dsColorNew.dangerRed : dsColorNew.textDark }}>
								{labelOf(row.site_id)} → reads <Box component="span" sx={codeSx}>{row.read_code || '?'}</Box>
								{row.cap != null ? ` · cap ${row.cap} %` : ' · no cap'} → writes <Box component="span" sx={codeSx}>{row.write_code || '?'}</Box>
								{(row.derived || []).length ? ` · derived: ${row.derived.map(d => (typeof d === 'string' ? d : `${d.code || d.pricelist_code || '?'} ${d.delta || ''}`.trim())).join(', ')}` : ''}
								{row.invalid ? ` · invalid: ${String(row.invalid).replace(/_/g, ' ')} — pick another write pricelist` : ''}
							</Typography>
						))}
					</Box>
				))}

				<H n={3}>Same searches before and after (criterion 37)</H>
				{eq ? (
					<Box sx={small}>
						<Typography sx={small}>
							At {fmtWhen(eq.now)}: the old model composed <strong>{eq.legacy_searches ?? '—'}</strong> searches, the new one <strong>{eq.new_searches ?? '—'}</strong> —{' '}
							{eq.equal ? <Box component="strong" sx={{ color: dsColorNew.successText }}>equal</Box> : <Box component="strong" sx={{ color: dsColorNew.noteAmber }}>they differ</Box>}.
						</Typography>
						{(eq.only_legacy || []).length > 0 && <Typography sx={muted}>Only the old model: {eq.only_legacy.map(String).join(', ')}</Typography>}
						{(eq.only_new || []).length > 0 && <Typography sx={muted}>Only the new model: {eq.only_new.map(String).join(', ')}</Typography>}
					</Box>
				) : <Typography sx={muted}><em>Not in the report.</em></Typography>}

				<H n={4}>Needs a hand</H>
				{(r.needs_hand || []).length === 0
					? <Typography sx={muted}>Nothing — every strategy fitted a product and a cadence.</Typography>
					: <Box component="ul" sx={{ m: 0, pl: 2.5, ...small }}>{r.needs_hand.map((t, i) => <li key={i}>{t}</li>)}</Box>}
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={onClose}>Close</AppDialogCancel>
			</AppDialogFooter>
		</AppDialog>
	)
}
